import { createFileRoute } from "@tanstack/react-router";
import { GrowthPlanSections } from "@/components/GrowthPlanSections";
import {
  IndustryMarketingPage,
  industryMarketingHead,
  type IndustryFAQ,
} from "@/components/IndustryMarketingPage";

const FAQS: IndustryFAQ[] = [
  {
    q: "How do junk removal companies get more jobs?",
    a: "Show up in the Google Maps top three for 'junk removal near me' in every town you serve, with clear pricing guidance, real truck and crew photos, and a fast way to book or text a photo for a quote.",
  },
  {
    q: "Should I make pages for each town I serve?",
    a: "Yes. Separate pages for Colorado Springs, Monument, Falcon, Fountain and other towns help you show up where people actually search.",
  },
  {
    q: "Is Google Ads worth it for junk removal?",
    a: "It can be, once your website and Google Maps are working. We focus on free leads first, then add ads to fill slow days. Ad spend not included.",
  },
  {
    q: "Do you help with dumpster rentals too?",
    a: "Yes. Many junk haulers also rent dumpsters; we build separate pages for each service so both rank.",
  },
];

export const Route = createFileRoute("/junk-removal-marketing-colorado")({
  head: () =>
    industryMarketingHead("Junk Removal", "junk removal", "junk-removal-marketing-colorado", FAQS),
  component: () => (
    <IndustryMarketingPage
      industry={"Junk Removal"}
      industryLower={"junk removal"}
      slug={"junk-removal-marketing-colorado"}
      subheadline={"We help Colorado junk removal and hauling companies fill their trucks with booked jobs from Google, Google Maps, AI search and social media — based in Colorado Springs."}
      painPoints={["Slow weeks with trucks sitting idle","You don't show up on Google Maps outside your home town","Paying for leads that go to several haulers at once"]}
      serviceDescriptions={{
        seo: "Rank for junk removal, furniture removal, estate cleanouts, hot tub removal and more in every town you serve.",
        googleMaps: "Show up in the Google Maps top three for 'junk removal near me' across your service area.",
        webDesign: "Fast, phone-first sites with text-a-photo quotes, pricing guidance and online booking.",
        ppc: "Ads that fill slow days, set up once your free channels are working. Ad spend not included.",
        geoAi: "Get named when people ask ChatGPT or Google AI for the best junk removal near them.",
        socialMedia: "Before-and-after videos of real jobs that get local views and repeat customers.",
      }}
      faqs={FAQS}
      extraSections={<GrowthPlanSections
          quoteIndustry="Junk Removal"
          eyebrow="Job booking"
          tipsHeading="How Colorado junk removal companies book more jobs"
          tips={[
            { title: "Text-a-photo quotes", body: "Let customers send a photo for a quick price. It's the easiest way to turn a search into a job." },
            { title: "Pricing guidance up front", body: "Show typical price ranges by load size. People book when they aren't guessing." },
            { title: "One page per town", body: "Pages for each town you serve help you show up on Google Maps beyond your home base." },
            { title: "Real truck and crew photos", body: "Your own trucks and team build trust that stock photos never will." },
            { title: "Reviews after every job", body: "Ask for a Google review the day of the pickup to keep climbing on Google Maps." },
            { title: "Before-and-after videos", body: "Short cleanout videos on social media earn local views and referrals for free." },
          ]}
          planHeading="Your 90-day local SEO plan for a Colorado junk removal company"
          plan={[
            { label: "Days 1–30", title: "Fix the foundation", items: ["Google Business Profile categories, service area, photos and services", "Fast, phone-first site with call, text and booking buttons", "Name, address and phone matched across directories"] },
            { label: "Days 31–60", title: "Build service and town pages", items: ["Pages for cleanouts, furniture, appliances, hot tubs and dumpsters", "Pages for each town in your service area", "Pricing questions answered in plain words"] },
            { label: "Days 61–90", title: "Grow reviews and reach", items: ["Review requests after every job", "Weekly Google Business Profile posts with job photos", "Track calls and bookings, then double down"] },
          ]}
          caseStudySlugs={["moving-colorado-springs", "cleaning-fort-collins"]}
          caseStudyNote="We don't have a published junk removal case study yet. Here are the closest real results: a Colorado Springs moving company and a Fort Collins cleaning business."
          links={[
            { to: "/learn/tips/junk-removal-marketing-tips-colorado", label: "Junk removal marketing tips for Colorado" },
            { to: "/junk-removal-web-design-colorado", label: "Junk removal web design" },
            { to: "/pikes-peak-contractor-marketing", label: "Pikes Peak contractor marketing" },
            { to: "/pricing", label: "Pricing" },
          ]}
        />}
      cityHubLinks={[{"to":"/colorado-springs","label":"Junk removal marketing in Colorado Springs","description":"Our home base — book more jobs across the Pikes Peak region."},{"to":"/castle-rock","label":"Junk removal marketing in Castle Rock","description":"Reach homeowners across Douglas County."},{"to":"/denver-digital-marketing","label":"Junk removal marketing in Denver","description":"Fill trucks across the Denver metro."}]}
    />
  ),
});
